import { useTranslation } from "react-i18next";
import { formatDate } from "../../../../utils/helpers";
import { SelectCountry } from "../../../../Component/Form/SelectCountry/SelectCountry";
import { useSearchParams } from "react-router-dom";
import { useLocalizedText } from "../../../../utils/hooks/useLocalizedText";
import { useCachedBasicData } from "@/utils/hooks/useCachedBasicData";
import { DATE_TIME_FORMAT, NUSUK_CITY_ID } from "@/utils/constants";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { PilgrimGroupNameMappingInfiniteSelect } from "@/Component/InfiniteSearchSelect/PilgrimGroupNameMappingInfiniteSelect";

export const useArrival = (date) => {
  // ========
  // hooks
  // ========
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const { cachedBasicData: basicData } = useCachedBasicData();
  const { getLocalizedText } = useLocalizedText();

  // ==========
  // variables
  // ==========
  const source = searchParams.get("source") || "delegation";

  // =================
  // label values
  // =================
  const nusukLabelValues = [
    {
      key: "capacity",
      label: t("ARRIVAL_CARD_PILGRIMS_COUNT"),
    },
    {
      key: "airline",
      label: t("ARRIVAL_CARD_AIRLINE_COMPANY"),
      render: (_, arrival) =>
        getLocalizedText(arrival?.airline_name_ar, arrival?.airline_name_en) ||
        "-",
    },
    {
      key: "airport_id",
      label: t("UPCOMING_COL_PORT"),
      render: (_, arrival) => {
        const entry_port = basicData?.ports?.find(
          (p) => p.id === arrival?.airport_id
        );
        return entry_port ? entry_port?.name : "-";
      },
    },

    {
      key: "flight_number",
      label: t("FLIGHTS_MODAL_FLIGHT_NO_LABEL"),
      render: (_, arrival) => {
        return arrival?.flight_number || "-";
      },
    },

    {
      key: "departure_time",
      label: t("AIRPORT_DEPARTURE_TIME"),
      render: (_, arrival) => {
        return arrival?.departure_time
          ? formatDate(
            arrival?.departure_time,
            i18n.language,
            "DD - MMMM YYYY HH:MM"
          )
          : "-";
      },
    },
    {
      key: "arrival_date",
      label: t("ARRIVAL_CARD_FLIGHT_DATE"),
      render: (_, arrival) => {
        return arrival?.arrival_date
          ? formatDate(
            arrival?.arrival_date,
            i18n.language,
            "DD - MMMM YYYY HH:MM"
          )
          : "-";
      },
    },
    {
      key: "contract_id",
      label: t("HOUSING_CONTRACT"),
    },
    {
      key: "organizer",
      label: t("ORGANIZER"),
    },
  ];
  const delegationLabelValues = [
    {
      key: "hjaj_number",
      label: t("ARRIVAL_CARD_PILGRIMS_COUNT"),
    },
    {
      key: "flight_number",
      label: t("FLIGHTS_MODAL_FLIGHT_NO_LABEL"),
      render: (_, arrival) => {
        return arrival?.flight_number || "-";
      },
    },
    {
      key: "Airline",
      label: t("ARRIVAL_CARD_AIRLINE_COMPANY"),
      render: (_, arrival) => <SelectCachedOptions.Display
        optionKey="airlines"
        value={arrival?.AirLine}
        campareBy="id"
        render={(option) => [getLocalizedText(option?.name_ar, option?.name_en), `(${option?.code})`].join(" ")} />,
    },
    {
      key: "scheduled_at",
      label: t("ARRIVAL_CARD_FLIGHT_DATE"),
      render: (_, arrival) => {
        return arrival.scheduled_at
          ? formatDate(arrival.scheduled_at, i18n.language, DATE_TIME_FORMAT)
          : "-";
      },
    },
    {
      key: "mission_name",
      label: t("ORGANIZER"),
    },
    {
      key: "company_name",
      label: t("AFFILIATED_ORGANIZER"),
      hide: (arrival) => arrival?.type !== 'group',
    },
    {
      key: "actual_arrival_time",
      label: t("ARRIVAL_CARD_ACTUAL_ARRIVAL_TIME"),
      render: (_, arrival) => {
        const real_datetime = arrival?.real_datetime;
        return !real_datetime
          ? "-"
          : formatDate(real_datetime, i18n.language, DATE_TIME_FORMAT);
      },
    },
    {
      key: "nationality",
      label: t("NATIONALITY"),
      render: (_, arrival) => (
        <SelectCountry.Display value={arrival?.nationality} />
      ),
    },
    {
      key: "number_of_accommodated",
      label: t("ACCOMMODATED_PILGRIMS"),
    },
    {
      key: "entry_port",
      label: t("UPCOMING_COL_PORT"),
      render: (value) => getLocalizedText(value?.name_ar, value?.name_en) || "-",
    },
    {
      key: "coming_from_id",
      label: t("ARRIVAL_CARD_ORIGIN"),
      render: (value) => <SelectCachedOptions.Display
        value={value}
        campareBy="id"
        optionKey="directions"
        render={(option) => option?.name}
      />,
    },
    {
      key: "direction_id",
      label: t("RECEPTION_CARD_DIRECTION_LABEL"),
      render: (value) => <SelectCachedOptions.Display
        value={value}
        campareBy="id"
        optionKey="directions"
        render={(option) => option?.name}
      />,
    },
    {
      key: "service_center",
      label: t("ARRIVAL_CARD_SERVICE_CENTER"),
      render: (_, arrival) => (getLocalizedText(arrival?.service_center?.name_ar, arrival?.service_center?.name_en) || '-'),
    },
  ];
  const betweenCitiesLabelValues = [
    {
      key: "passengers_count",
      label: t("PASSENGER_COUNT"),
    },
    {
      key: "boarding_order",
      label: t("BOARDING_ORDER"),
    },
    {
      key: "transport_company_id",
      label: t("TRANSPORT_COMPANY"),
    },
    {
      key: "route",
      label: t("ROUTE"),
    },
    {
      key: "boarding_time",
      label: t("BOARDING_TIME"),
    },
    {
      key: "housing_contracts",
      label: t("HOUSING_CONTRACT"),
    },
    {
      key: "guide_name",
      label: t("GUIDE_NAME"),
    },
    {
      key: "guide_mobile",
      label: t("MOBILE_NUMBER"),
    },
  ];


  const filterFields = [
    {
      name: "date",
      label: t("DATE"),
      type: "date",
    },
    {
      name: "nationality_id",
      label: t("NATIONALITY"),
      type: "select",
      optionKey: "nationalities",
      options: (n) => ({
        value: n.id,
        label: <SelectCountry.Display value={n.id} />,
      }),
    },
    {
      name: "campaign_number",
      label: t("CAMPAIGN_NUMBER"),
      type: "input",
    },
    {
      name: "group_number",
      label: t("GROUP_NUMBER"),
      type: "input",
    },
    {
      name: "flight_number",
      label: t("FLIGHT_NUMBER"),
      type: "input",
    },
    {
      name: "airline_id",
      label: t("FLIGHT_AIRLINE"),
      type: "select",
      optionKey: "airlines",
      options: (a) => ({
        value: a.id,
        label: `${getLocalizedText(a?.name_ar, a?.name_en)} (${a.code})`,
      }),
    },
    {
      name: "service_center_id",
      label: t("SERVICE_CENTER"),
      type: "select",
      optionKey: "service_centers",
      options: (sc) => ({
        value: sc.id,
        label: sc.name,
      }),
    },
    {
      name: "entry_port",
      label: t("ENTRY_PORT"),
      type: "select",
      optionKey: "ports",
      options: (ep) => ({
        value: ep.id,
        label: getLocalizedText(ep?.name_ar, ep?.name_en) || ep?.name,
      }),
    },
    {
      name: "direction_id",
      label: t("DIRECTION"),
      type: "select",
      optionKey: "directions",
      options: (d) => ({
        value: d.id,
        label: [d.id, d.name].join(" - "),
      }),
    },
    (watch, setValue) => ({
      name: "mission_id",
      label: t("ORGANIZER"),
      type: "select",
      optionKey: "missions",
      options: (m) => ({
        value: m.id,
        label: getLocalizedText(m.name_ar, m.name_en) || m.name,
      }),
      onChange: () => {
        setValue("group_name_mapping_id", null);
      },
    }),
    (watch, setValue) => ({
      name: "company_id",
      label: t("AFFILIATED_ORGANIZER"),
      type: "select",
      optionKey: "companies",
      options: (c) => ({
        value: c.id,
        label: getLocalizedText(c.name_ar, c.name_en) || c.name,
      }),
      onChange: () => {
        setValue("group_name_mapping_id", null);
      },
    }),
    (watch) => ({
      name: "group_name_mapping_id",
      label: t("MAPPED_GROUP_NAME"),
      type: "infiniteSelect",
      Component: (componentProps) => (
        <PilgrimGroupNameMappingInfiniteSelect
          {...componentProps}
          mission_id={watch("mission_id")}
          company_id={watch("company_id")}
        />
      ),
      placeholder: t("MAPPED_GROUP_NAME"),
    }),
  ];
  const nusukFilterFields = [
    {
      name: "request_number",
      label: t("ARRIVAL_CARD_REQUEST_NUMBER"),
      type: "text",
      placeholder: t("ARRIVAL_CARD_REQUEST_NUMBER"),
    },
    {
      name: "flight_date",
      label: t("FLIGHT_DATE"),
      type: "date",
    },
    {
      name: "arrival_date_from",
      type: "date",
      optionKey: "directions",
      label: t("DATE_FROM"),
    },
    {
      name: "arrival_date_to",
      type: "date",
      optionKey: "directions",
      label: t("DATE_TO"),
    },
    {
      name: "contract_id",
      type: "select",
      optionKey: "contracts",
      label: t("HOUSING_CONTRACT"),
      filter: (c) => String(c.city_id) === String(NUSUK_CITY_ID.madina),
      options: (c) => ({
        value: c.id,
        label: [c.id, c.house_commercial_name_ar].join(" - "),
      }),

    },
    {
      name: "nationality_id",
      label: t("NATIONALITY"),
      type: "select",
      optionKey: "nationalities",
      options: (n) => ({
        value: n.id,
        label: <SelectCountry.Display value={n.id} />,
      }),
    },
    {
      name: "airport_code",
      label: t("UPCOMING_COL_PORT"),
      type: "select",
      optionKey: "ports",
      options: (n) => ({
        value: n.code,
        label: [getLocalizedText(n?.name_ar, n?.name_en) || n?.name, n?.code].join(" - "),
      }),
    },
    (watch, setValue) => ({
      name: "mission_id",
      label: t("ORGANIZER"),
      type: "select",
      optionKey: "missions",
      options: (m) => ({
        value: m.id,
        label: getLocalizedText(m.name_ar, m.name_en) || m.name,
      }),
      onChange: () => {
        setValue("group_name_mapping_id", null);
      },
    }),
    (watch, setValue) => ({
      name: "company_id",
      label: t("AFFILIATED_ORGANIZER"),
      type: "select",
      optionKey: "companies",
      options: (c) => ({
        value: c.id,
        label: getLocalizedText(c.name_ar, c.name_en) || c.name,
      }),
      onChange: () => {
        setValue("group_name_mapping_id", null);
      },
    }),
    (watch) => ({
      name: "group_name_mapping_id",
      label: t("MAPPED_GROUP_NAME"),
      type: "infiniteSelect",
      Component: (componentProps) => (
        <PilgrimGroupNameMappingInfiniteSelect
          {...componentProps}
          mission_id={watch("mission_id")}
          company_id={watch("company_id")}
        />
      ),
      placeholder: t("MAPPED_GROUP_NAME"),
    }),
  ];

  const title = {
    group: t("ARRIVAL_CARD_GROUP_NUMBER"),
    campaign: t("ARRIVAL_CARD_CAMPAIGN_NUMBER"),
    nusuk: t("ARRIVAL_CARD_REQUEST_NUMBER"),
    between_cities: t("ARRIVAL_CARD_BETWEEN_CITIES_NUMBER"),
  };

  // const type = isNusuk ? "request_number" : arrivalType;
  // const value = isNusuk ? arrival?.request_number : serial;
  const labelValues = {
    nusuk: nusukLabelValues,
    between_cities: betweenCitiesLabelValues,
    campaign: delegationLabelValues,
    delegation: delegationLabelValues,
  };

  // =================
  // return values
  // =================
  return {
    labelValues: labelValues[source] ?? [],
    filterFields: source === "nusuk" ? nusukFilterFields : filterFields,
    title,
    source,
  };
};
